import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import {
  getInvoicePayments,
  createInvoicePayment,
  deleteInvoicePayment,
  calculateTotalPayments
} from '../../services/invoicePaymentManagementService';
import { IInvoicePayment } from '../../types/invoicePayment';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

interface Account {
  _id: string;
  name: string;
  accountNumber?: string;
  currentBalance: number;
}

interface InvoicePaymentManagementDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
  invoiceTotal: number;
  cashAccountId?: string;
  cashPaidAmount?: number;
  paymentMethodsTotal?: number;
}

const InvoicePaymentManagementDialog: React.FC<InvoicePaymentManagementDialogProps> = ({
  open,
  onClose,
  invoiceId,
  invoiceNumber,
  invoiceTotal,
  cashAccountId,
  cashPaidAmount = 0,
  paymentMethodsTotal = 0
}) => {
  const { user } = useAuth();
  const isSystemAdmin = user?.isSystemAdmin || false;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [payments, setPayments] = useState<IInvoicePayment[]>([]);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentNotes, setPaymentNotes] = useState<string>('');

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, invoiceId, cashPaidAmount, cashAccountId, paymentMethodsTotal]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // جلب الدفعات
      const paymentsData = await getInvoicePayments(invoiceId);
      setPayments(paymentsData);

      // حساب المدفوع من الدفعات
      const paidFromPayments = calculateTotalPayments(paymentsData);
      
      // إضافة المبلغ المدفوع نقداً
      const cashPaid = Number(cashPaidAmount || 0);
      
      // إضافة إجمالي طرق الدفع
      const paymentMethodsPaid = Number(paymentMethodsTotal || 0);
      
      // حساب إجمالي المدفوع
      const totalPaidAmount = paidFromPayments + cashPaid + paymentMethodsPaid;
      setTotalPaid(totalPaidAmount);
      
      // حساب المتبقي
      const remaining = Math.max(0, Number(invoiceTotal) - totalPaidAmount);
      setRemainingAmount(remaining);
      
      console.log('📊 حساب المتبقي في Dialog:');
      console.log('   - إجمالي الفاتورة:', invoiceTotal);
      console.log('   - المدفوع من الدفعات:', paidFromPayments);
      console.log('   - المدفوع نقداً:', cashPaid);
      console.log('   - المدفوع من طرق الدفع:', paymentMethodsPaid);
      console.log('   - إجمالي المدفوع:', totalPaidAmount);
      console.log('   - المتبقي:', remaining);

      // جلب الحسابات
      try {
        const accountsResponse = await axios.get('/api/finance/accounts', {
          headers: {
            'x-permission-context': 'invoice-payment'
          }
        });
        
        let availableAccounts: Account[] = [];
        
        // التعامل مع البنية المختلفة للاستجابة
        if (Array.isArray(accountsResponse.data)) {
          availableAccounts = accountsResponse.data;
        } else if (accountsResponse.data?.data) {
          if (Array.isArray(accountsResponse.data.data)) {
            availableAccounts = accountsResponse.data.data;
          } else if (accountsResponse.data.data.accounts) {
            availableAccounts = accountsResponse.data.data.accounts;
          }
        }
        
        // تحويل الحسابات إذا كان المعرف بحقل مختلف
        availableAccounts = availableAccounts.map(acc => ({
          _id: acc._id || (acc as any).id || '',
          name: acc.name || '',
          accountNumber: acc.accountNumber,
          currentBalance: acc.currentBalance || 0
        }));
        
        setAccounts(availableAccounts);

        // تعيين حساب الصندوق تلقائياً
        if (cashAccountId) {
          const cashAccountExists = availableAccounts.some(acc => acc._id === cashAccountId);
          if (cashAccountExists) {
            setSelectedAccountId(cashAccountId);
          } else if (availableAccounts.length > 0) {
            setSelectedAccountId(availableAccounts[0]._id);
          }
        } else if (availableAccounts.length > 0) {
          setSelectedAccountId(availableAccounts[0]._id);
        }
      } catch (accountsError) {
        console.error('خطأ في جلب الحسابات:', accountsError);
        setAccounts([]);
      }

    } catch (err: any) {
      console.error('خطأ في تحميل البيانات:', err);
      setError(err.response?.data?.message || err.message || 'حدث خطأ أثناء تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    try {
      const amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('يرجى إدخال مبلغ صحيح');
        return;
      }

      if (amount > remainingAmount) {
        setError(`المبلغ المدخل (${amount.toFixed(2)}) أكبر من المبلغ المتبقي (${remainingAmount.toFixed(2)})`);
        return;
      }

      if (!selectedAccountId) {
        setError('يرجى اختيار حساب');
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      await createInvoicePayment({
        invoiceId: invoiceId,
        invoiceNumber: invoiceNumber,
        amount: amount,
        accountId: selectedAccountId, // إضافة معرف الحساب
        notes: paymentNotes || undefined
      });

      await loadData();

      setPaymentAmount('');
      setPaymentNotes('');
      
      setSuccess('تم إضافة الدفعة والمعاملة المالية بنجاح');

    } catch (err: any) {
      console.error('خطأ في إضافة الدفعة:', err);
      setError(err.response?.data?.message || err.message || 'حدث خطأ أثناء إضافة الدفعة');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الدفعة؟')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await deleteInvoicePayment(paymentId);
      await loadData();

      setSuccess('تم حذف الدفعة بنجاح');

    } catch (err: any) {
      console.error('خطأ في حذف الدفعة:', err);
      setError(err.response?.data?.message || err.message || 'حدث خطأ أثناء حذف الدفعة');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ar });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir="rtl"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" />
            <Typography variant="h6">
              تسديد الفاتورة - {invoiceNumber}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && payments.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* معلومات الفاتورة */}
            <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي الفاتورة
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(Number(invoiceTotal))}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    المبلغ المدفوع
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(totalPaid)}
                  </Typography>
                  {(Number(cashPaidAmount) > 0 || Number(paymentMethodsTotal) > 0) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {Number(cashPaidAmount) > 0 && `نقدي: ${formatCurrency(Number(cashPaidAmount))}`}
                      {Number(cashPaidAmount) > 0 && Number(paymentMethodsTotal) > 0 && ' + '}
                      {Number(paymentMethodsTotal) > 0 && `طرق دفع: ${formatCurrency(Number(paymentMethodsTotal))}`}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    المبلغ المتبقي
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {formatCurrency(remainingAmount)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* قسم إضافة دفعة جديدة */}
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="primary" />
                إضافة دفعة جديدة
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* عرض خانة الحسابات للجميع - للقراءة فقط للمستخدمين العاديين */}
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" error={accounts.length === 0}>
                    <InputLabel>الحساب</InputLabel>
                    <Select
                      value={selectedAccountId}
                      onChange={(e) => setSelectedAccountId(e.target.value)}
                      label="الحساب"
                      startAdornment={<AccountBalanceIcon sx={{ mr: 1, color: 'action.active' }} />}
                      disabled={!isSystemAdmin || accounts.length === 0}
                    >
                      {accounts.length === 0 ? (
                        <MenuItem value="" disabled>
                          لا توجد حسابات متاحة
                        </MenuItem>
                      ) : (
                        accounts.map((account) => (
                          <MenuItem key={account._id} value={account._id}>
                            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{account.name}</span>
                                {cashAccountId === account._id && (
                                  <Chip
                                    label="حساب الصندوق"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {/* عرض الرصيد فقط لمدير النظام */}
                              {isSystemAdmin && (
                                <Chip
                                  label={formatCurrency(account.currentBalance)}
                                  size="small"
                                  color={account.currentBalance >= 0 ? 'success' : 'error'}
                                />
                              )}
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {!isSystemAdmin && (
                    <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                      ملاحظة: الحساب للقراءة فقط. فقط مدير النظام يمكنه تغيير الحساب.
                    </Typography>
                  )}
                  {accounts.length === 0 && isSystemAdmin && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      تحذير: لا توجد حسابات متاحة. يرجى التحقق من الصلاحيات أو إضافة حسابات في النظام.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="المبلغ"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ملاحظات (اختياري)"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddPayment}
                    disabled={loading || remainingAmount <= 0 || !selectedAccountId}
                    fullWidth
                  >
                    إضافة دفعة
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* جدول الدفعات */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                الدفعات المسجلة ({payments.length + (Number(cashPaidAmount) > 0 ? 1 : 0) + (Number(paymentMethodsTotal) > 0 ? 1 : 0)})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {payments.length === 0 && Number(cashPaidAmount) <= 0 && Number(paymentMethodsTotal) <= 0 ? (
                <Alert severity="info">
                  لا توجد دفعات مسجلة لهذه الفاتورة
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>التاريخ</TableCell>
                        <TableCell align="right">المبلغ</TableCell>
                        <TableCell>الملاحظات</TableCell>
                        <TableCell>المستخدم</TableCell>
                        <TableCell align="center">الإجراءات</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* عرض إجمالي طرق الدفع إن وجدت */}
                      {Number(paymentMethodsTotal) > 0 && (
                        <TableRow sx={{ bgcolor: 'info.50' }}>
                          <TableCell>
                            <Chip
                              label="طرق الدفع"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="info.main">
                              {formatCurrency(Number(paymentMethodsTotal))}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              إجمالي المدفوع عبر طرق الدفع (بطاقة، تحويل، إلخ)
                            </Typography>
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell align="center">
                            <Chip
                              label="مدفوع"
                              size="small"
                              color="info"
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {/* عرض الدفعة النقدية المسبقة إن وجدت */}
                      {Number(cashPaidAmount) > 0 && (
                        <TableRow sx={{ bgcolor: 'success.50' }}>
                          <TableCell>
                            <Chip
                              label="دفعة نقدية"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(Number(cashPaidAmount))}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              مبلغ مدفوع نقداً عند إنشاء الفاتورة
                            </Typography>
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell align="center">
                            <Chip
                              label="مدفوع"
                              size="small"
                              color="success"
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {/* عرض الدفعات المسجلة */}
                      {payments.map((payment) => (
                        <TableRow key={payment._id} hover>
                          <TableCell>{formatDate(payment.createdAt || payment.paymentDate)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {payment.notes || '-'}
                          </TableCell>
                          <TableCell>
                            {payment.createdByName || '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="حذف الدفعة">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeletePayment(payment._id!)}
                                disabled={loading}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentManagementDialog;
